/**
 * Filename: src/components/SkillsTable.js
 *
 * Purpose:  Presentational component that renders the Qualifications / Skills matrix
 *           as an accessible HTML <table>, plus an optional list of capability
 *           highlights. The component holds no data of its own — content is supplied
 *           by src/data/qualifications.js so copy and markup stay decoupled.
 *
 * Usage:    import SkillsTable from '../components/SkillsTable';
 *           import { skillsMatrix, capabilityHighlights } from '../data/qualifications';
 *
 *           <SkillsTable
 *               title="Qualifications & Skills"
 *               rows={skillsMatrix}
 *               highlights={capabilityHighlights}
 *           />
 */

import React from 'react';
import { SKILL_LEVELS } from '../data/qualifications';

/**
 * Maps a skill-level label to its badge modifier class.
 * Kept outside the component so the object is allocated once, not per render.
 */
const LEVEL_CLASS_MAP = {
    [SKILL_LEVELS.TOP]: 'skill-badge--top',
    [SKILL_LEVELS.EXCELLENT]: 'skill-badge--excellent',
    [SKILL_LEVELS.VERY_STRONG]: 'skill-badge--very-strong',
    [SKILL_LEVELS.STRONG]: 'skill-badge--strong'
};

/**
 * Resolve the CSS modifier class for a given skill level.
 *
 * @param {string} level - A label from SKILL_LEVELS.
 * @returns {string} The badge class name, falling back to the neutral variant
 *                   so an unrecognized level still renders legibly.
 */
function getLevelClass(level) {
    return LEVEL_CLASS_MAP[level] || 'skill-badge--strong';
}

/**
 * Renders the qualifications matrix and capability highlights.
 *
 * The table uses scope="col" / scope="row" and a <caption> so screen readers can
 * announce the header for each cell. On narrow viewports the CSS collapses each
 * row into a stacked card, using the data-label attributes below as row labels.
 *
 * @param {Object}   props
 * @param {string}   [props.title]      - Heading shown above the table.
 * @param {Array}    props.rows         - Skill rows; see src/data/qualifications.js.
 * @param {Array}    [props.highlights] - Optional capability highlight entries.
 * @returns {JSX.Element|null} Null when there are no rows to display.
 */
export default function SkillsTable({ title = 'Qualifications & Skills', rows = [], highlights = [] }) {
    if (rows.length === 0) {
        return null;
    }

    return (
        <section className="skills-section" aria-labelledby="skills-heading">
            <h4 className="skills-heading" id="skills-heading">{title}</h4>

            <div className="skills-table-wrapper">
                <table className="skills-table">
                    <caption className="sr-only">
                        Qualifications matrix listing each skill area, the capability
                        demonstrated, the proficiency level, and years of experience.
                    </caption>
                    <thead>
                        <tr>
                            <th scope="col">Area</th>
                            <th scope="col">Demonstrated Capability</th>
                            <th scope="col">Skill Level</th>
                            <th scope="col">Experience</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.id}>
                                <th scope="row" data-label="Area">{row.area}</th>
                                <td data-label="Demonstrated Capability">{row.capability}</td>
                                <td data-label="Skill Level">
                                    <span className={`skill-badge ${getLevelClass(row.level)}`}>
                                        {row.level}
                                    </span>
                                </td>
                                <td data-label="Experience">{row.experience}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {highlights.length > 0 && (
                <div className="skills-highlights">
                    <h5 className="skills-highlights-title">Additional Hands-On Experience</h5>
                    <ul className="skills-highlights-list">
                        {highlights.map(item => (
                            <li key={item.id}>{item.text}</li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}
